import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, ChevronRight, ChevronDown, X, Sparkles, BookOpen, Target, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import ProductionContextWizard from './ProductionContextWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * OnboardingChecklist - Guia de Primeiros Passos
 * 
 * Mostra ao usuário novo o que precisa configurar para aproveitar
 * todos os benefícios da plataforma HeveaTalent.
 * 
 * Baseado no Tripé: Justiça, Segurança, Simplicidade
 */
const OnboardingChecklist = ({ user, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState({
    hasProperty: false,
    hasContext: false,
    hasRecords: false,
    hasEvaluation: false,
    hasCertification: false,
    loading: true
  });
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(true);
  
  // Estado para o Wizard de Contexto de Produção
  const [showContextWizard, setShowContextWizard] = useState(false);
  const [wizardData, setWizardData] = useState({
    properties: [],
    contexts: [],
    clones: [],
    tappingSystems: []
  });

  // Verificar progresso do usuário
  useEffect(() => {
    const checkProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Verificar propriedades
        const propsRes = await fetch(`${API}/bleeding-properties`, { headers });
        const properties = await propsRes.json();
        const hasProperty = Array.isArray(properties) && properties.length > 0;

        // Verificar contexto de produção (se tem propriedade)
        let hasContext = false;
        if (hasProperty && properties[0]?.id) {
          try {
            const contextRes = await fetch(`${API}/contextual-performance/contexts/${properties[0].id}`, { headers });
            if (contextRes.ok) {
              const context = await contextRes.json();
              hasContext = context && context.clone_type;
            }
          } catch (e) {
            hasContext = false;
          }
        }

        // Verificar registros no calendário
        let hasRecords = false;
        if (hasProperty && properties[0]?.id) {
          try {
            const recordsRes = await fetch(`${API}/bleeding-records/${properties[0].id}`, { headers });
            const records = await recordsRes.json();
            hasRecords = Array.isArray(records) && records.length > 0;
          } catch (e) {
            hasRecords = false;
          }
        }

        // Verificar avaliações recebidas OU selo já atribuído
        let hasEvaluation = false;
        try {
          // Check if user already has a seal (from /me data)
          if (user.current_seal && user.current_seal !== 'none') {
            hasEvaluation = true;
          } else {
            const evalRes = await fetch(`${API}/evaluations/users/${user.id}/metrics`, { headers });
            if (evalRes.ok) {
              const metrics = await evalRes.json();
              hasEvaluation = (metrics.total_evaluations > 0) || (metrics.current_seal && metrics.current_seal !== 'none');
            }
          }
        } catch (e) {
          hasEvaluation = false;
        }

        // Verificar certificação LGPD (opt-in)
        let hasCertification = false;
        try {
          if (user.certification_opt_in === true) {
            hasCertification = true;
          } else {
            const certRes = await fetch(`${API}/certification/status`, { headers });
            if (certRes.ok) {
              const cert = await certRes.json();
              hasCertification = cert.certification_status !== 'not_opted' || cert.consent_given === true;
            }
          }
        } catch (e) {
          hasCertification = false;
        }

        setProgress({
          hasProperty,
          hasContext,
          hasRecords,
          hasEvaluation,
          hasCertification,
          loading: false
        });

        // Mostrar modal de boas-vindas para usuários novos
        const isNew = !hasProperty && !hasRecords && !hasEvaluation;
        const hasSeenWelcome = localStorage.getItem('heveatalent_welcome_seen');
        if (isNew && !hasSeenWelcome) {
          setShowWelcomeModal(true);
        }

      } catch (error) {
        console.error('Erro ao verificar progresso:', error);
        setProgress(prev => ({ ...prev, loading: false }));
      }
    };

    if (user?.id) {
      checkProgress();
    }

    // Escutar eventos de atualização (propriedade criada, registro salvo, etc.)
    const handleProgressUpdate = () => {
      if (user?.id) {
        checkProgress();
      }
    };
    window.addEventListener('heveatalent-progress-update', handleProgressUpdate);
    return () => {
      window.removeEventListener('heveatalent-progress-update', handleProgressUpdate);
    };
  }, [user]);

  // Função para abrir o Wizard de Contexto de Produção
  const openContextWizard = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Carregar dados necessários para o wizard
      const [propsRes, contextsRes, clonesRes, systemsRes] = await Promise.all([
        fetch(`${API}/bleeding-properties`, { headers }),
        fetch(`${API}/contextual-performance/contexts`, { headers }),
        fetch(`${API}/contextual-performance/reference/clones`, { headers }),
        fetch(`${API}/contextual-performance/reference/tapping-systems`, { headers })
      ]);
      
      const properties = await propsRes.json();
      const contextsData = await contextsRes.json();
      const clonesData = await clonesRes.json();
      const systemsData = await systemsRes.json();
      
      setWizardData({
        properties: Array.isArray(properties) ? properties : [],
        contexts: contextsData.contexts || [],
        clones: clonesData.clones || [],
        tappingSystems: systemsData.systems || []
      });
      
      setShowContextWizard(true);
    } catch (error) {
      console.error('Erro ao carregar dados do wizard:', error);
    }
  };

  // Callback quando o wizard for concluído
  const handleWizardComplete = () => {
    setShowContextWizard(false);
    // Atualizar o progresso
    setProgress(prev => ({ ...prev, hasContext: true }));
    // Recarregar a página para atualizar todos os componentes
    window.location.reload();
  };

  // Calcular progresso total
  const completedSteps = [
    progress.hasProperty,
    progress.hasContext,
    progress.hasRecords,
    progress.hasEvaluation,
    progress.hasCertification
  ].filter(Boolean).length;

  const totalSteps = 5;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  // Não mostrar se dismissiu
  if (dismissed) {
    return null;
  }

  // Loading state
  if (progress.loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-green-100 p-6 mb-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }

  // Passos do onboarding
  const steps = [
    {
      id: 'property',
      title: t('onboardingStep1Title') || 'Cadastre sua Propriedade',
      description: t('onboardingStep1Desc') || 'Configure sua fazenda com nome, localização e área de produção',
      completed: progress.hasProperty,
      action: () => onNavigate?.('calendar'),
      icon: '🏡',
      priority: 1
    },
    {
      id: 'context',
      title: t('onboardingStep2Title') || 'Configure o Contexto de Produção',
      description: t('onboardingStep2Desc') || 'Defina clone, sistema de sangria e número de árvores para metas justas',
      completed: progress.hasContext,
      action: openContextWizard, // Agora abre o wizard
      icon: '⚙️',
      priority: 2,
      requires: 'property'
    },
    {
      id: 'records',
      title: t('onboardingStep3Title') || 'Registre suas Sangrias',
      description: t('onboardingStep3Desc') || 'Use o Calendário para registrar produção diária e acompanhar evolução',
      completed: progress.hasRecords,
      action: () => onNavigate?.('calendar'),
      icon: '📅',
      priority: 3,
      requires: 'property'
    },
    {
      id: 'certification',
      title: t('onboardingStep4Title') || 'Aceite os Termos LGPD',
      description: t('onboardingStep4Desc') || 'Configure suas preferências de privacidade e apareça no Balcão de Empregos',
      completed: progress.hasCertification,
      action: () => navigate('/certification'),
      icon: '🔒',
      priority: 4
    },
    {
      id: 'evaluation',
      title: t('onboardingStep5Title') || 'Receba sua Primeira Avaliação',
      description: t('onboardingStep5Desc') || 'Convide um técnico ou proprietário para avaliar seu trabalho e ganhar seu selo',
      completed: progress.hasEvaluation,
      action: () => navigate('/evaluations'),
      icon: '🏆',
      priority: 5
    }
  ];

  // Encontrar próximo passo
  const nextStep = steps.find(s => !s.completed && (!s.requires || progress[`has${s.requires.charAt(0).toUpperCase() + s.requires.slice(1)}`]));

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleCloseWelcome = () => {
    setShowWelcomeModal(false);
    localStorage.setItem('heveatalent_welcome_seen', 'true');
  };

  return (
    <>
      {/* Modal de Boas-vindas */}
      {showWelcomeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* Header com gradiente */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8" />
                <h2 className="text-2xl font-bold">{t('welcomeModalTitle') || 'Bem-vindo ao HeveaTalent!'}</h2>
              </div>
              <p className="text-green-100">
                {t('welcomeModalSubtitle') || 'A plataforma completa para heveicultores'}
              </p>
            </div>

            {/* Conteúdo */}
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                {t('welcomeModalText') || 'Estamos felizes em ter você conosco! Preparamos um guia rápido para você aproveitar todos os benefícios da plataforma.'}
              </p>

              {/* Destaques */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <BookOpen className="w-5 h-5 text-green-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-800">{t('welcomeFeature1Title') || 'Calendário de Sangria'}</h4>
                    <p className="text-sm text-gray-600">{t('welcomeFeature1Desc') || 'Registre produção, controle equipe e acompanhe evolução'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Target className="w-5 h-5 text-blue-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800">{t('welcomeFeature2Title') || 'Metas Contextuais'}</h4>
                    <p className="text-sm text-gray-600">{t('welcomeFeature2Desc') || 'Metas justas baseadas no seu clone, sistema e região'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
                  <div className="bg-amber-100 p-2 rounded-lg">
                    <Award className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800">{t('welcomeFeature3Title') || 'Sistema de Selos'}</h4>
                    <p className="text-sm text-gray-600">{t('welcomeFeature3Desc') || 'Conquiste Bronze, Prata ou Ouro e destaque-se'}</p>
                  </div>
                </div>
              </div>

              {/* Tripé */}
              <div className="flex justify-center gap-4 mb-6">
                <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">⚖️ Justiça</span>
                <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full">🔒 Segurança</span>
                <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full">✨ Simplicidade</span>
              </div>

              {/* Botão */}
              <button
                onClick={handleCloseWelcome}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                {t('welcomeModalButton') || 'Começar Agora'} 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card de Checklist */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6 mb-6 relative">
        {/* Botão de fechar */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          title={t('dismissChecklist') || 'Ocultar checklist'}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header - clicável para expandir/minimizar quando 100% */}
        <div 
          className={`flex items-center gap-3 ${progressPercent === 100 ? 'cursor-pointer' : ''} ${progressPercent === 100 && !minimized ? 'mb-4' : progressPercent < 100 ? 'mb-4' : ''}`}
          onClick={() => progressPercent === 100 && setMinimized(!minimized)}
        >
          <div className="bg-green-600 text-white p-2 rounded-lg">
            {progressPercent === 100 ? <CheckCircle2 className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-green-800 text-lg">
              {progressPercent === 100 
                ? (t('onboardingCompleteTitle') || 'Primeiros Passos Completos!')
                : (t('onboardingTitle') || 'Primeiros Passos')
              }
            </h3>
            <p className="text-sm text-gray-600">
              {progressPercent === 100
                ? (t('onboardingCompleteDesc') || 'Todas as etapas foram concluídas com sucesso')
                : (t('onboardingSubtitle') || 'Complete estas etapas para aproveitar todos os benefícios')
              }
            </p>
          </div>
          {progressPercent === 100 && (
            <button className="text-green-600 hover:text-green-800 transition-colors p-1">
              {minimized ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          )}
        </div>

        {/* Conteúdo expansível - minimizado quando 100% completo */}
        {(progressPercent < 100 || !minimized) && (
          <>
            {/* Barra de Progresso */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">{t('progress') || 'Progresso'}</span>
                <span className="font-semibold text-green-700">{completedSteps}/{totalSteps} {t('completed') || 'concluídos'}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Lista de Passos */}
            <div className="space-y-3">
              {steps.map((step, index) => {
                const isLocked = step.requires && !progress[`has${step.requires.charAt(0).toUpperCase() + step.requires.slice(1)}`];
                const isNext = nextStep?.id === step.id;

                return (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      step.completed
                        ? 'bg-green-100 border border-green-200'
                        : isNext
                        ? 'bg-white border-2 border-green-400 shadow-sm'
                        : isLocked
                        ? 'bg-gray-50 opacity-60'
                        : 'bg-white border border-gray-200'
                    }`}
                  >
                    {/* Ícone de status */}
                    <div className={`flex-shrink-0 ${step.completed ? 'text-green-600' : isLocked ? 'text-gray-400' : 'text-gray-400'}`}>
                      {step.completed ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        <Circle className="w-6 h-6" />
                      )}
                    </div>

                    {/* Ícone do passo */}
                    <span className="text-2xl flex-shrink-0">{step.icon}</span>

                    {/* Conteúdo */}
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-semibold ${step.completed ? 'text-green-800' : 'text-gray-800'}`}>
                        {step.title}
                      </h4>
                      <p className="text-xs text-gray-500 truncate">{step.description}</p>
                    </div>

                    {/* Botão de ação */}
                    {!step.completed && !isLocked && (
                      <button
                        onClick={step.action}
                        data-testid={`checklist-action-${step.id}`}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          isNext
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {isNext ? (t('start') || 'Iniciar') : (t('configure') || 'Configurar')}
                        <ChevronRight className="w-4 h-4 inline ml-1" />
                      </button>
                    )}

                    {/* Badge de bloqueado */}
                    {isLocked && (
                      <span className="flex-shrink-0 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        🔒 {t('requiresPrevious') || 'Requer passo anterior'}
                      </span>
                    )}

                    {/* Badge de concluído */}
                    {step.completed && (
                      <span className="flex-shrink-0 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                        ✓ {t('done') || 'Feito'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Dica */}
            {nextStep && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  💡 <strong>{t('nextTip') || 'Próximo passo'}:</strong> {nextStep.description}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Wizard de Contexto de Produção */}
      <ProductionContextWizard
        isOpen={showContextWizard}
        properties={wizardData.properties}
        contexts={wizardData.contexts}
        clones={wizardData.clones}
        tappingSystems={wizardData.tappingSystems}
        onComplete={handleWizardComplete}
        onCancel={() => setShowContextWizard(false)}
      />
    </>
  );
};

export default OnboardingChecklist;
